import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import FetchService from "@core-ui/ApiServices/FetchService";
import { refreshPage } from "@core-ui/utils/initGlobalFuncs";
import type { RefreshAvailableCatalogsResult } from "@ext/enterprise/types/AvailableCatalog";
import { create } from "zustand";
import { addEvent, Level } from "../../../extensions/loggers/opentelemetry";

export type AvailableCatalogsStatus = "idle" | "loading" | "ok" | "error";

interface AvailableCatalogsStore {
	status: AvailableCatalogsStatus;
	/** `ApiUrlCreatorService.value` is a React context read: take it during render and pass it in, it fails here. */
	load: (apiUrlCreator: ApiUrlCreator) => Promise<void>;
}

const requestAvailableCatalogs = async (apiUrlCreator: ApiUrlCreator): Promise<RefreshAvailableCatalogsResult> => {
	try {
		const res = await FetchService.fetch<RefreshAvailableCatalogsResult>(
			apiUrlCreator.refreshAvailableCatalogs(),
			undefined,
			undefined,
			undefined,
			false,
		);
		return res.ok ? await res.json() : { status: "error", changed: false };
	} catch (error) {
		addEvent("failed to refresh available catalogs", Level.Important, {
			error: error instanceof Error ? error.message : String(error),
		});
		return { status: "error", changed: false };
	}
};

export const useAvailableCatalogsStore = create<AvailableCatalogsStore>((set, get) => ({
	status: "idle",
	load: async (apiUrlCreator) => {
		if (get().status === "loading") return;
		set({ status: "loading" });
		const { status, changed } = await requestAvailableCatalogs(apiUrlCreator);
		if (changed) await refreshPage();
		set({ status });
	},
}));
